import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"

// The "Einsätze bearbeiten" dialog for the timeline layout. Opens on the `planning:edit-spans`
// document event (dispatched by planning-timeline#editSpans) with the employee/plannable identity,
// the set_spans URL, and the full list of the employee's runs on that plannable. Each run becomes an
// editable Von/Bis row; rows can be added/removed. Save PATCHes the COMPLETE set to set_spans, which
// reconciles the underlying daily allocations and re-renders the site section.
export default class extends Controller {
  static targets = [
    "root",
    "name",
    "count",
    "rows",
    "rowTemplate",
    "workingDaysOnly",
    "submit",
    "spinner",
  ]
  static values = { countOne: String, countOther: String }

  connect() {
    this.current = null
    this.boundOpen = this.open.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("planning:edit-spans", this.boundOpen)
  }

  disconnect() {
    document.removeEventListener("planning:edit-spans", this.boundOpen)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  open(event) {
    const detail = event.detail || {}
    this.current = {
      url: detail.setSpansUrl,
      plannableType: detail.plannableType,
      plannableId: detail.plannableId,
      // "Fleet" lanes post fleet_id; everything else is an employee lane.
      resourceType: detail.resourceType || "Employee",
      employeeId: detail.employeeId,
      fleetId: detail.fleetId,
    }
    this.nameTarget.textContent = [detail.employeeName || detail.fleetName, detail.plannableName]
      .filter(Boolean)
      .join(" · ")

    this.rowsTarget.innerHTML = ""
    const spans = detail.spans && detail.spans.length ? detail.spans : [{ from: "", to: "" }]
    spans.forEach((span) => this.appendRow(span.from, span.to))
    this.workingDaysOnlyTarget.checked = true
    this.updateCount()

    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    document.addEventListener("keydown", this.boundKeydown)
  }

  addRow() {
    this.appendRow("", "")
  }

  appendRow(from, to) {
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    if (from) fragment.querySelector("[data-spans-from]").value = from
    if (to) fragment.querySelector("[data-spans-to]").value = to
    this.rowsTarget.appendChild(fragment)
    this.updateCount()
  }

  removeRow(event) {
    event.target.closest("[data-spans-row]")?.remove()
    this.updateCount()
  }

  // Reflect the current number of Von/Bis rows in the header badge, using the
  // pluralized label templates provided by the component (no German in JS).
  updateCount() {
    if (!this.hasCountTarget) return
    const count = this.rowsTarget.querySelectorAll("[data-spans-row]").length
    const template = count === 1 ? this.countOneValue : this.countOtherValue
    this.countTarget.textContent = template.replace("{count}", count)
  }

  close() {
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    this.current = null
    this.setPending(false)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  // Disable Save + show its spinner while the set_spans request is in flight; the dialog stays open
  // until the server replies (see post()).
  setPending(pending) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = pending
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !pending)
  }

  // Escape closes the dialog, but only when it is the topmost layer: with a date field's
  // calendar popover open, that keystroke belongs to the calendar. The picker marks the event
  // handled (preventDefault) when it dismisses its own panel; the :popover-open check also
  // covers the native light-dismiss, which closes the popover without touching the event.
  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.rootTarget.querySelector(":popover-open")) return

    this.close()
  }

  // Where the press STARTED, so a click only counts as a backdrop click when both ends of it
  // are on the backdrop. Selecting the text of a date field by dragging routinely slips past
  // the panel's edge, and the resulting click — whose target is the nearest common ancestor,
  // i.e. the backdrop — used to close the dialog and discard everything the user had entered.
  backdropPress(event) {
    this.pressedOnBackdrop = event.target === this.rootTarget
  }

  backdrop(event) {
    const startedOnBackdrop = this.pressedOnBackdrop
    this.pressedOnBackdrop = false
    if (event.target === this.rootTarget && startedOnBackdrop) this.close()
  }

  save() {
    if (!this.current) return
    const spans = [...this.rowsTarget.querySelectorAll("[data-spans-row]")]
      .map((row) => ({
        date_from: row.querySelector("[data-spans-from]").value,
        date_to: row.querySelector("[data-spans-to]").value,
      }))
      .filter((span) => span.date_from && span.date_to)

    const body = {
      plannable_type: this.current.plannableType,
      plannable_id: Number(this.current.plannableId),
      spans,
      working_days_only: this.workingDaysOnlyTarget.checked ? "1" : "0",
    }
    if (this.current.resourceType === "Fleet") {
      body.fleet_id = Number(this.current.fleetId)
    } else {
      body.employee_id = Number(this.current.employeeId)
    }
    this.post(this.current.url, body)
  }

  async post(url, body) {
    this.setPending(true)
    const { ok } = await planningMutation({
      url,
      method: "PATCH",
      body,
      component: "planning-spans-dialog",
    })
    if (ok) {
      this.close()
    } else {
      this.setPending(false)
    }
  }
}
